import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react'
import type { StudioEditorHost } from '@genieartinc/core-ports'
import type { StudioAsset, StudioFrame, StudioFrameOutput, StudioProject, StudioSnapshot, StudioWebPage } from '@genieartinc/schemas/graph'
import { appendStudioFrameVersion, readStudioProject, webPageToStudioGraph } from '@genieartinc/core-adapters/studio'
import { createHttpStudioHost } from '@genieartinc/core-adapters/studio/http'

import type { StudioContextValue, StudioMode } from './node-editor/studio-types'
export type { StudioContextValue, StudioMode } from './node-editor/studio-types'
export const StudioContext = createContext<StudioContextValue | null>(null)
export function useStudio() {
  const value = useContext(StudioContext)
  if (!value) throw new Error('Frame nodes require an open Genie project.')
  return value
}

export function useStudioProject(canvasId: string | null, onShowNode: (graphId: string, nodeId: string) => void, suppliedHost?: StudioEditorHost): StudioContextValue {
  const host = useMemo(() => suppliedHost ?? (canvasId ? createHttpStudioHost(import.meta.env.VITE_API_BASE_URL, canvasId) : null), [canvasId, suppliedHost])
  const [snapshot, setSnapshot] = useState<StudioSnapshot | null>(null)
  const current = useRef<StudioSnapshot | null>(null)
  const [busy, setBusy] = useState(false)
  const busyRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<StudioMode>('flow')
  const [selectedFrameId, selectFrame] = useState<string | null>(null)
  const [focusFrameId, setFocusFrameId] = useState<string | null>(null)
  const urls = useRef(new Map<string, string>())
  const generation = useRef(0)
  const reload = useCallback(async () => {
    if (!host) return
    const epoch = generation.current
    setBusy(true)
    try {
      const loaded = await host.load()
      if (epoch !== generation.current) return
      current.current = loaded; setSnapshot(loaded); setError(null)
    } catch (e) { if (epoch === generation.current) setError((e as Error).message) }
    finally { if (epoch === generation.current) setBusy(false) }
  }, [host])
  useEffect(() => {
    generation.current++
    current.current = null; setSnapshot(null); setError(null); setMode('flow'); selectFrame(null); setFocusFrameId(null)
    void reload()
    return () => { generation.current++; for (const url of urls.current.values()) URL.revokeObjectURL(url); urls.current.clear() }
  }, [reload])
  const mutate = useCallback(async (operation: (project: StudioProject) => Promise<StudioProject> | StudioProject) => {
    if (!host || !current.current) throw new Error('Wait for the project to load.')
    if (busyRef.current) throw new Error('A project save is already in progress. Please retry after it finishes.')
    busyRef.current = true; setBusy(true); setError(null)
    const epoch = generation.current
    const before = current.current
    try {
      const project = readStudioProject(await operation(before.project))
      if (epoch !== generation.current) throw new Error('The project was closed before saving.')
      // Retain the complete draft if the server rejects a stale revision.
      current.current = { revision: before.revision, project }; setSnapshot(current.current)
      const saved = await host.save({ expectedRevision: before.revision, project })
      if (epoch === generation.current) { current.current = saved; setSnapshot(saved) }
    } catch (e) { if (epoch === generation.current) setError((e as Error).message); throw e }
    finally { busyRef.current = false; if (epoch === generation.current) setBusy(false) }
  }, [host])
  const resolveAsset = useCallback(async (id: string) => {
    const asset = current.current?.project.assets.find(a => a.id === id)
    if (!asset || !host) throw new Error('This version has no available asset.')
    const cached = urls.current.get(asset.sha256)
    if (cached) return cached
    const epoch = generation.current
    const bytes = await host.readAsset(asset)
    if (epoch !== generation.current) throw new Error('The project was closed.')
    const existing = urls.current.get(asset.sha256)
    if (existing) return existing
    const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: asset.mimeType }))
    urls.current.set(asset.sha256, url)
    return url
  }, [host])
  const upload = useCallback(async (blob: Blob, name: string): Promise<StudioAsset> => {
    if (!host) throw new Error('No project is open.')
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(blob.type)) throw new Error('Choose a PNG, JPEG, or WebP image.')
    if (blob.size > 24 * 1024 * 1024) throw new Error('The image exceeds the 24 MB upload limit.')
    const bytes = new Uint8Array(await blob.arrayBuffer())
    const hash = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
    const sha256 = [...hash].map(n => n.toString(16).padStart(2, '0')).join('')
    const asset: StudioAsset = { id: sha256, name, mediaType: 'image', mimeType: blob.type, size: bytes.length, sha256 }
    const prior = current.current?.project.assets.find(a => a.sha256 === sha256)
    if (prior) return prior
    await host.putAsset(asset, bytes)
    return asset
  }, [host])
  const importImage = useCallback(async (file: File) => {
    await mutate(async project => {
      const asset = await upload(file, file.name)
      const bitmap = await createImageBitmap(file)
      const frame: StudioFrame = { id: crypto.randomUUID(), title: file.name, kind: 'media', x: project.artboards.length * 560, y: 0, w: 512, h: Math.max(32, 512 * bitmap.height / bitmap.width), activeVersion: 0, params: { width: bitmap.width, height: bitmap.height }, versions: [{ id: crypto.randomUUID(), label: 'Original', params: {}, assetId: asset.id }] }
      bitmap.close(); setFocusFrameId(frame.id)
      return { ...project, artboards: [...project.artboards, frame], assets: project.assets.some(a => a.id === asset.id) ? project.assets : [...project.assets, asset] }
    })
  }, [mutate, upload])
  const patchFrames = useCallback((patches: { id: string; patch: Partial<StudioFrame> }[]) => mutate(project => ({ ...project, artboards: project.artboards.map(frame => { const patch = patches.find(p => p.id === frame.id)?.patch; return patch ? { ...frame, ...patch, id: frame.id } : frame }) })), [mutate])
  const changeFrame = useCallback((frame: StudioFrame) => mutate(project => ({ ...project, artboards: project.artboards.map(f => f.id === frame.id ? frame : f) })), [mutate])
  const writeOutput = useCallback(async (output: StudioFrameOutput, source: string, page: StudioWebPage, graphId: string) => {
    await mutate(async project => {
      // Fetch in the client: the hosted service never fetches an arbitrary imported URL.
      const response = await fetch(source)
      if (!response.ok) throw new Error('The output image could not be read. The frame was not changed.')
      const asset = await upload(await response.blob(), output.version.label || 'Workflow output')
      const snapshotId = `${graphId}:${output.version.id}`
      const graph = { ...webPageToStudioGraph(snapshotId, 'Workflow output', page), sourceGraphId: graphId }
      const prepared = { ...project, graphs: [...project.graphs.filter(g => g.id !== graph.id), graph], assets: project.assets.some(a => a.id === asset.id) ? project.assets : [...project.assets, asset] }
      return appendStudioFrameVersion(prepared, { ...output, version: { ...output.version, assetId: asset.id, ...(output.version.provenance ? { provenance: { ...output.version.provenance, graphId: snapshotId } } : {}) } })
    })
  }, [mutate, upload])
  return { project: snapshot?.project ?? null, busy, error, mode, setMode, selectedFrameId, selectFrame, patchFrames, focusFrameId,
    showFrame: useCallback((id: string) => { setFocusFrameId(id); selectFrame(id); setMode('board') }, []),
    showNode: useCallback((graphId: string, id: string) => { setMode('flow'); const graph = current.current?.project.graphs.find(g => g.id === graphId); onShowNode(typeof graph?.sourceGraphId === 'string' ? graph.sourceGraphId : graphId, id) }, [onShowNode]),
    resolveAsset, importImage, changeFrame, writeOutput, reload }
}

export function useStudioAsset(assetId: string | undefined) {
  const { resolveAsset } = useStudio()
  const [result, setResult] = useState<{ url?: string; error?: string }>({})
  useEffect(() => {
    let cancelled = false; setResult({})
    if (assetId) void resolveAsset(assetId).then(url => { if (!cancelled) setResult({ url }) }, e => { if (!cancelled) setResult({ error: (e as Error).message }) })
    return () => { cancelled = true }
  }, [assetId, resolveAsset])
  return result
}
